/**
 * useOnChainPortfolio Hook
 *
 * Manages on-chain portfolio data:
 * - Wallet CRUD operations
 * - Data syncing from DeBank
 * - Portfolio summary, tokens, DeFi positions
 * - Transaction history
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import type {
  OnChainWallet,
  OnChainToken,
  OnChainDefiPosition,
  OnChainTransaction,
  OnChainDailySnapshot,
  OnChainNFT,
  OnChainApproval,
  OnChainHistoryTx,
  OnChainUsedChain,
  OnChainPortfolioStats,
  SyncStatus,
  TransactionFilters,
  PaginationParams,
  PaginatedResponse,
} from '../types/onchain';

// =====================================================
// NEW TYPES FOR NET CURVE & CHAIN BALANCE
// =====================================================

export interface NetCurvePoint {
  timestamp: number;
  usd_value: number;
}

export interface NetCurveData {
  wallet_id: string;
  wallet_address: string;
  curve: NetCurvePoint[];
  change_24h_usd: number;
  change_24h_percent: number;
  current_value: number;
  value_24h_ago: number;
}

export interface ChainBalanceToken {
  token_id: string;
  chain: string;
  symbol: string;
  name: string;
  logo_url: string | null;
  decimals: number;
  amount: number;
  price: number;
  value_usd: number;
  is_core: boolean;
  is_verified: boolean;
}

export interface ChainBalanceData {
  chain_id: string;
  chain_name: string;
  chain_logo: string | null;
  total_usd_value: number;
  tokens: ChainBalanceToken[];
}

// =====================================================
// TYPES
// =====================================================

export interface OnChainPortfolioSummary {
  wallets: OnChainWallet[];
  totalValueUsd: number;
  totalCostBasis: number;
  totalPnL: number;
  totalPnLPercent: number;
  unrealizedPnL: number;
  realizedPnL: number;
  change24hUsd: number;
  change24hPercent: number;
  valueInBtc: number;
  btcPrice: number;
  vsBtcPercent: number;
  tokensCount: number;
  chainsCount: number;
  walletsCount: number;
  lastSyncAt: string | null;
  syncStatus: SyncStatus;
}

export interface UseOnChainPortfolioReturn {
  // Data
  summary: OnChainPortfolioSummary | null;
  tokens: OnChainToken[];
  defiPositions: OnChainDefiPosition[];
  transactions: PaginatedResponse<OnChainTransaction> | null;
  snapshots: OnChainDailySnapshot[];
  nfts: OnChainNFT[];
  approvals: OnChainApproval[];
  history: PaginatedResponse<OnChainHistoryTx> | null;
  usedChains: OnChainUsedChain[];
  stats: OnChainPortfolioStats | null;
  netCurve: NetCurveData | null;
  chainBalance: ChainBalanceData | null;

  // Loading states
  isLoading: boolean;
  isSyncing: boolean;
  isFetchingCurve: boolean;
  isFetchingChainBalance: boolean;
  error: Error | null;
  clearError: () => void;

  // Wallet management
  wallets: OnChainWallet[];
  addWallet: (address: string, label?: string) => Promise<boolean>;
  updateWallet: (walletId: string, data: { label?: string; is_primary?: boolean; include_in_homepage?: boolean }) => Promise<boolean>;
  toggleHomepageInclusion: (walletId: string, include: boolean) => Promise<boolean>;
  deleteWallet: (walletId: string) => Promise<boolean>;

  // Data fetching
  fetchSummary: (walletId?: string) => Promise<void>;
  fetchTokens: (walletId?: string) => Promise<void>;
  fetchDefiPositions: (walletId?: string) => Promise<void>;
  fetchTransactions: (walletId?: string, filters?: TransactionFilters, pagination?: PaginationParams) => Promise<void>;
  fetchSnapshots: (walletId?: string, pagination?: PaginationParams) => Promise<void>;
  fetchNFTs: (walletId?: string) => Promise<void>;
  fetchApprovals: (walletId?: string) => Promise<void>;
  fetchHistory: (walletId?: string, filters?: TransactionFilters, pagination?: PaginationParams) => Promise<void>;
  fetchUsedChains: (walletId?: string) => Promise<void>;
  fetchStats: (walletId?: string) => Promise<void>;
  fetchNetCurve: (walletId?: string) => Promise<void>;
  fetchChainBalance: (walletId?: string, chainId?: string) => Promise<void>;

  // Sync
  syncWallet: (walletId?: string) => Promise<boolean>;
  syncAll: () => Promise<void>;

  // Selected wallet
  selectedWalletId: string | null;
  setSelectedWalletId: (id: string | null) => void;

  // Auth state
  isLoggedIn: boolean;
}

// =====================================================
// API HELPERS
// =====================================================

async function apiCall<T>(
  endpoint: string,
  body: Record<string, unknown>,
  accessToken: string
): Promise<T> {
  const response = await fetch(`/api/onchain/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `API error: ${response.status}`);
  }

  return response.json();
}

// =====================================================
// HOOK
// =====================================================

export function useOnChainPortfolio(): UseOnChainPortfolioReturn {
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Data state
  const [summary, setSummary] = useState<OnChainPortfolioSummary | null>(null);
  const [wallets, setWallets] = useState<OnChainWallet[]>([]);
  const [tokens, setTokens] = useState<OnChainToken[]>([]);
  const [defiPositions, setDefiPositions] = useState<OnChainDefiPosition[]>([]);
  const [transactions, setTransactions] = useState<PaginatedResponse<OnChainTransaction> | null>(null);
  const [snapshots, setSnapshots] = useState<OnChainDailySnapshot[]>([]);
  const [nfts, setNFTs] = useState<OnChainNFT[]>([]);
  const [approvals, setApprovals] = useState<OnChainApproval[]>([]);
  const [history, setHistory] = useState<PaginatedResponse<OnChainHistoryTx> | null>(null);
  const [usedChains, setUsedChains] = useState<OnChainUsedChain[]>([]);
  const [stats, setStats] = useState<OnChainPortfolioStats | null>(null);
  const [netCurve, setNetCurve] = useState<NetCurveData | null>(null);
  const [chainBalance, setChainBalance] = useState<ChainBalanceData | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isFetchingCurve, setIsFetchingCurve] = useState(false);
  const [isFetchingChainBalance, setIsFetchingChainBalance] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);

  // Refs
  const initialLoadDone = useRef(false);

  // =====================================================
  // AUTH
  // =====================================================

  useEffect(() => {
    const getToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setAccessToken(session?.access_token || null);
      } catch (e) {
        console.warn('[OnChain] Failed to get session:', e);
        setAccessToken(null);
      }
    };

    // Always try to get the session on mount, don't wait for isAuthenticated
    getToken();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAccessToken(session?.access_token || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // No dependency on isAuthenticated - run once on mount

  // =====================================================
  // WALLET MANAGEMENT
  // =====================================================

  const fetchWallets = useCallback(async () => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ wallets: OnChainWallet[] }>(
        'wallets',
        { action: 'list' },
        accessToken
      );
      setWallets(data.wallets);

      // Select primary wallet by default
      if (!selectedWalletId && data.wallets.length > 0) {
        const primary = data.wallets.find(w => w.is_primary) || data.wallets[0];
        setSelectedWalletId(primary.id);
      }
    } catch (e) {
      console.error('[OnChain] Failed to fetch wallets:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch wallets'));
    }
  }, [accessToken, selectedWalletId]);

  const addWallet = useCallback(async (address: string, label?: string): Promise<boolean> => {
    if (!accessToken) return false;

    try {
      const data = await apiCall<{ wallet: OnChainWallet }>(
        'wallets',
        { action: 'add', wallet_address: address, label },
        accessToken
      );

      setWallets(prev => [...prev, data.wallet]);

      // If this is the first wallet, select it
      if (wallets.length === 0) {
        setSelectedWalletId(data.wallet.id);
      }

      return true;
    } catch (e) {
      console.error('[OnChain] Failed to add wallet:', e);
      setError(e instanceof Error ? e : new Error('Failed to add wallet'));
      return false;
    }
  }, [accessToken, wallets.length]);

  const updateWallet = useCallback(async (
    walletId: string,
    data: { label?: string; is_primary?: boolean; include_in_homepage?: boolean }
  ): Promise<boolean> => {
    if (!accessToken) return false;

    try {
      const result = await apiCall<{ wallet: OnChainWallet }>(
        'wallets',
        { action: 'update', wallet_id: walletId, ...data },
        accessToken
      );

      setWallets(prev => prev.map(w =>
        w.id === walletId ? result.wallet : (data.is_primary ? { ...w, is_primary: false } : w)
      ));

      return true;
    } catch (e) {
      console.error('[OnChain] Failed to update wallet:', e);
      setError(e instanceof Error ? e : new Error('Failed to update wallet'));
      return false;
    }
  }, [accessToken]);

  /**
   * Toggle whether a wallet is included in homepage aggregation
   */
  const toggleHomepageInclusion = useCallback(async (
    walletId: string,
    include: boolean
  ): Promise<boolean> => {
    return updateWallet(walletId, { include_in_homepage: include });
  }, [updateWallet]);

  const deleteWallet = useCallback(async (walletId: string): Promise<boolean> => {
    if (!accessToken) return false;

    try {
      await apiCall<{ message: string }>(
        'wallets',
        { action: 'delete', wallet_id: walletId },
        accessToken
      );

      setWallets(prev => prev.filter(w => w.id !== walletId));

      // If deleted wallet was selected, select another
      if (selectedWalletId === walletId) {
        const remaining = wallets.filter(w => w.id !== walletId);
        setSelectedWalletId(remaining.length > 0 ? remaining[0].id : null);
      }

      return true;
    } catch (e) {
      console.error('[OnChain] Failed to delete wallet:', e);
      setError(e instanceof Error ? e : new Error('Failed to delete wallet'));
      return false;
    }
  }, [accessToken, selectedWalletId, wallets]);

  // =====================================================
  // DATA FETCHING
  // =====================================================

  const fetchSummary = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ summary: OnChainPortfolioSummary }>(
        'portfolio',
        { action: 'summary', wallet_id: walletId },
        accessToken
      );
      setSummary(data.summary);

      // Update wallets list from summary
      if (data.summary.wallets) {
        setWallets(data.summary.wallets);
      }
    } catch (e) {
      console.error('[OnChain] Failed to fetch summary:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch summary'));
    }
  }, [accessToken]);

  const fetchTokens = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ tokens: OnChainToken[] }>(
        'portfolio',
        { action: 'tokens', wallet_id: walletId },
        accessToken
      );
      setTokens(data.tokens);
    } catch (e) {
      console.error('[OnChain] Failed to fetch tokens:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch tokens'));
    }
  }, [accessToken]);

  const fetchDefiPositions = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ positions: OnChainDefiPosition[] }>(
        'portfolio',
        { action: 'positions', wallet_id: walletId },
        accessToken
      );
      setDefiPositions(data.positions);
    } catch (e) {
      console.error('[OnChain] Failed to fetch DeFi positions:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch DeFi positions'));
    }
  }, [accessToken]);

  const fetchTransactions = useCallback(async (
    walletId?: string,
    filters?: TransactionFilters,
    pagination?: PaginationParams
  ) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<PaginatedResponse<OnChainTransaction>>(
        'portfolio',
        { action: 'transactions', wallet_id: walletId, filters, pagination },
        accessToken
      );
      setTransactions(data);
    } catch (e) {
      console.error('[OnChain] Failed to fetch transactions:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch transactions'));
    }
  }, [accessToken]);

  const fetchSnapshots = useCallback(async (walletId?: string, pagination?: PaginationParams) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ data: OnChainDailySnapshot[] }>(
        'portfolio',
        { action: 'snapshots', wallet_id: walletId, pagination },
        accessToken
      );
      setSnapshots(data.data);
    } catch (e) {
      console.error('[OnChain] Failed to fetch snapshots:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch snapshots'));
    }
  }, [accessToken]);

  const fetchNFTs = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ nfts: OnChainNFT[] }>(
        'portfolio',
        { action: 'nfts', wallet_id: walletId },
        accessToken
      );
      setNFTs(data.nfts);
    } catch (e) {
      console.error('[OnChain] Failed to fetch NFTs:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch NFTs'));
    }
  }, [accessToken]);

  const fetchApprovals = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ approvals: OnChainApproval[] }>(
        'portfolio',
        { action: 'approvals', wallet_id: walletId },
        accessToken
      );
      setApprovals(data.approvals);
    } catch (e) {
      console.error('[OnChain] Failed to fetch approvals:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch approvals'));
    }
  }, [accessToken]);

  const fetchHistory = useCallback(async (
    walletId?: string,
    filters?: TransactionFilters,
    pagination?: PaginationParams
  ) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<PaginatedResponse<OnChainHistoryTx>>(
        'portfolio',
        { action: 'history', wallet_id: walletId, filters, pagination },
        accessToken
      );
      setHistory(data);
    } catch (e) {
      console.error('[OnChain] Failed to fetch history:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch history'));
    }
  }, [accessToken]);

  const fetchUsedChains = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ chains: OnChainUsedChain[] }>(
        'portfolio',
        { action: 'used_chains', wallet_id: walletId },
        accessToken
      );
      setUsedChains(data.chains);
    } catch (e) {
      console.error('[OnChain] Failed to fetch used chains:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch used chains'));
    }
  }, [accessToken]);

  const fetchStats = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    try {
      const data = await apiCall<{ stats: OnChainPortfolioStats }>(
        'portfolio',
        { action: 'stats', wallet_id: walletId },
        accessToken
      );
      setStats(data.stats);
    } catch (e) {
      console.error('[OnChain] Failed to fetch stats:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch stats'));
    }
  }, [accessToken]);

  /**
   * Fetch 24h portfolio value curve
   * Uses /v1/user/chain_net_curve from DeBank
   */
  const fetchNetCurve = useCallback(async (walletId?: string) => {
    if (!accessToken) return;

    setIsFetchingCurve(true);

    try {
      const data = await apiCall<{ results: NetCurveData[] }>(
        'sync',
        { action: 'fetch_net_curve', wallet_id: walletId },
        accessToken
      );

      // Take first result (or combine if multiple wallets)
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        setNetCurve(result);

        // Update summary with 24h change data
        if (summary) {
          setSummary({
            ...summary,
            change24hUsd: result.change_24h_usd,
            change24hPercent: result.change_24h_percent,
          });
        }
      }
    } catch (e) {
      console.error('[OnChain] Failed to fetch net curve:', e);
      // Don't set error - curve is optional enhancement
    } finally {
      setIsFetchingCurve(false);
    }
  }, [accessToken, summary]);

  /**
   * Fetch detailed token balance for a specific chain
   * Uses /v1/user/chain_balance from DeBank
   */
  const fetchChainBalance = useCallback(async (walletId?: string, chainId?: string) => {
    if (!accessToken) return;
    if (!chainId) {
      console.warn('[OnChain] fetchChainBalance requires chainId');
      return;
    }

    setIsFetchingChainBalance(true);

    try {
      const data = await apiCall<{ results: Array<{ wallet_id: string; chain_balance: ChainBalanceData | null }> }>(
        'sync',
        { action: 'fetch_chain_balance', wallet_id: walletId, chain_id: chainId },
        accessToken
      );

      // Take first result
      if (data.results && data.results.length > 0 && data.results[0].chain_balance) {
        setChainBalance(data.results[0].chain_balance);
      }
    } catch (e) {
      console.error('[OnChain] Failed to fetch chain balance:', e);
      setError(e instanceof Error ? e : new Error('Failed to fetch chain balance'));
    } finally {
      setIsFetchingChainBalance(false);
    }
  }, [accessToken]);

  // =====================================================
  // SYNC
  // =====================================================

  const syncWallet = useCallback(async (walletId?: string): Promise<boolean> => {
    if (!accessToken) return false;

    setIsSyncing(true);
    setError(null);

    try {
      await apiCall<{ message: string; results: unknown[] }>(
        'sync',
        { action: 'sync', wallet_id: walletId },
        accessToken
      );

      // Refresh all data after sync
      await Promise.all([
        fetchSummary(walletId),
        fetchTokens(walletId),
        fetchDefiPositions(walletId),
        fetchNFTs(walletId),
        fetchApprovals(walletId),
        fetchHistory(walletId),
        fetchUsedChains(walletId),
        fetchStats(walletId),
      ]);

      return true;
    } catch (e) {
      console.error('[OnChain] Sync failed:', e);
      setError(e instanceof Error ? e : new Error('Sync failed'));
      return false;
    } finally {
      setIsSyncing(false);
    }
  }, [accessToken, fetchSummary, fetchTokens, fetchDefiPositions, fetchNFTs, fetchApprovals, fetchHistory, fetchUsedChains, fetchStats]);

  const syncAll = useCallback(async () => {
    await syncWallet();
  }, [syncWallet]);

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    const loadInitialData = async () => {
      if (!accessToken || initialLoadDone.current) return;

      initialLoadDone.current = true;
      setIsLoading(true);
      setError(null);

      try {
        // Fetch wallets first
        await fetchWallets();

        // Then fetch summary which includes wallet data
        await fetchSummary();

        // Also fetch tokens for overview display
        await fetchTokens();
      } catch (e) {
        console.error('[OnChain] Initial load failed:', e);
        setError(e instanceof Error ? e : new Error('Failed to load data'));
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [accessToken, fetchWallets, fetchSummary, fetchTokens]);

  // Reset when token changes (logout/login)
  useEffect(() => {
    if (!accessToken) {
      initialLoadDone.current = false;
      setSummary(null);
      setWallets([]);
      setTokens([]);
      setDefiPositions([]);
      setTransactions(null);
      setSnapshots([]);
      setNFTs([]);
      setApprovals([]);
      setHistory(null);
      setUsedChains([]);
      setStats(null);
      setNetCurve(null);
      setChainBalance(null);
      setSelectedWalletId(null);
      setIsLoading(false);
      setIsFetchingCurve(false);
      setIsFetchingChainBalance(false);
      setError(null);
    }
  }, [accessToken]);

  // =====================================================
  // ERROR MANAGEMENT
  // =====================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =====================================================
  // RETURN
  // =====================================================

  return {
    // Data
    summary,
    tokens,
    defiPositions,
    transactions,
    snapshots,
    nfts,
    approvals,
    history,
    usedChains,
    stats,
    netCurve,
    chainBalance,

    // Loading states
    isLoading,
    isSyncing,
    isFetchingCurve,
    isFetchingChainBalance,
    error,
    clearError,

    // Wallet management
    wallets,
    addWallet,
    updateWallet,
    toggleHomepageInclusion,
    deleteWallet,

    // Data fetching
    fetchSummary,
    fetchTokens,
    fetchDefiPositions,
    fetchTransactions,
    fetchSnapshots,
    fetchNFTs,
    fetchApprovals,
    fetchHistory,
    fetchUsedChains,
    fetchStats,
    fetchNetCurve,
    fetchChainBalance,

    // Sync
    syncWallet,
    syncAll,

    // Selected wallet
    selectedWalletId,
    setSelectedWalletId,

    // Auth state
    isLoggedIn: !!accessToken,
  };
}

export default useOnChainPortfolio;
